var count_down_default = (() => {
    if (customElements.get("count-down")) return;
    const DAY_SECONDS = 86400;
    const ROLL = {
        duration: 300,
        distance: 1,
        blur: 0,
        clip: true,
        easing: "cubic-bezier(0.4, 0, 0.2, 1)"
    };
    const RARITY = {
        days: 1.6,
        hours: 1.2,
        minutes: 1,
        seconds: .55
    };
    const ROLLOVER_BOOST = 1.6;
    const STAGGER_MS = 40;
    const generations = new WeakMap;
    const tracked = new WeakMap;
    const buildDigitCells = (el, value) => {
        el.textContent = "";
        return [ ...value ].map(char => {
            const cell = document.createElement("span");
            cell.style.display = "inline-grid";
            const digit = document.createElement("span");
            digit.style.gridArea = "1 / 1";
            digit.setAttribute("data-digit", "");
            digit.textContent = char;
            cell.appendChild(digit);
            el.appendChild(cell);
            return digit;
        });
    };
    const prep = (cell, digitEl) => {
        const gen = (generations.get(cell) || 0) + 1;
        generations.set(cell, gen);
        for (const anim of tracked.get(cell) || []) anim.cancel();
        tracked.set(cell, []);
        for (const node of cell.querySelectorAll("[data-transient]")) node.remove();
        digitEl.style.opacity = "";
        return gen;
    };
    const track = (cell, ...anims) => {
        tracked.set(cell, [ ...tracked.get(cell) || [], ...anims ]);
    };
    const isCurrent = (cell, gen) => generations.get(cell) === gen;
    const makeLayer = (cell, char) => {
        const span = document.createElement("span");
        span.style.gridArea = "1 / 1";
        span.setAttribute("data-transient", "");
        span.textContent = char;
        cell.appendChild(span);
        return span;
    };
    const settle = (cell, digitEl) => {
        digitEl.style.opacity = "";
        for (const node of cell.querySelectorAll("[data-transient]")) node.remove();
    };
    const rollDigit = (cell, digitEl, newChar, params, delay) => {
        const gen = prep(cell, digitEl);
        const oldChar = digitEl.textContent;
        digitEl.textContent = newChar;
        digitEl.style.opacity = "0";
        const oldLayer = makeLayer(cell, oldChar);
        const newLayer = makeLayer(cell, newChar);
        const frame = (y, opacity, blur) => {
            const keyframe = {
                transform: `translateY(${y}em)`,
                opacity: opacity
            };
            if (params.blur > 0) keyframe.filter = `blur(${blur}px)`;
            return keyframe;
        };
        const options = {
            duration: params.duration,
            delay: delay,
            easing: ROLL.easing,
            fill: "forwards"
        };
        const out = oldLayer.animate([ frame(0, 1, 0), frame(-params.distance, 0, params.blur) ], options);
        const inn = newLayer.animate([ frame(params.distance, 0, params.blur), frame(0, 1, 0) ], options);
        track(cell, out, inn);
        Promise.allSettled([ out.finished, inn.finished ]).then(() => {
            if (isCurrent(cell, gen)) settle(cell, digitEl);
        });
    };
    const fill = (template, vars) => Object.entries(vars).reduce((out, [key, value]) => out.replaceAll(`__${key}__`, value), template);
    let pluralRules;
    const pluralForm = (forms, count) => {
        if (!forms) return "";
        pluralRules ??= new Intl.PluralRules(Shopify?.locale || "en");
        return forms[pluralRules.select(count)] ?? forms.other;
    };
    const composeAriaName = (strings, label, remaining) => {
        const days = Math.floor(remaining / DAY_SECONDS);
        const hours = Math.floor(remaining % DAY_SECONDS / 3600);
        const minutes = Math.floor(remaining % 3600 / 60);
        const seconds = remaining % 60;
        const unit = (key, count) => fill(pluralForm(strings.unit[key], count), {
            count: String(count)
        });
        const time = remaining >= DAY_SECONDS ? fill(strings.ariaTimeDays, {
            days: unit("day", days),
            hours: unit("hour", hours),
            minutes: unit("minute", minutes)
        }) : fill(strings.ariaTimeSubday, {
            hours: unit("hour", hours),
            minutes: unit("minute", minutes),
            seconds: unit("second", seconds)
        });
        return label ? fill(strings.ariaLabelWithLabel, {
            label: label,
            time: time
        }) : fill(strings.ariaLabel, {
            time: time
        });
    };
    customElements.define("count-down", class Countdown extends HTMLElement {
        constructor() {
            super();
            this.tick = this.tick.bind(this);
            this.onIntersect = this.onIntersect.bind(this);
            this.reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
        }
        connectedCallback() {
            this.deadline = Number(this.dataset.deadline);
            if (!Number.isFinite(this.deadline) || this.deadline <= 0) return;
            this.rolling = this.dataset.countdownEffects === "roll";
            this.units = new Map;
            for (const el of this.querySelectorAll("[data-countdown-unit]")) this.units.set(el.dataset.countdownUnit, el);
            this.digits = new Map;
            if (this.rolling) for (const [unit, el] of this.units) this.digits.set(unit, buildDigitCells(el, el.textContent.trim()));
            this.separators = new Map;
            for (const el of this.querySelectorAll("[data-countdown-separator]")) this.separators.set(el.dataset.countdownSeparator, el);
            this.captions = new Map;
            for (const el of this.querySelectorAll("[data-countdown-caption]")) this.captions.set(el.dataset.countdownCaption, el);
            this.showDays = null;
            this.ordinal = 0;
            this.visible = true;
            this.forceAria = true;
            this.lastAriaBucket = null;
            if ("IntersectionObserver" in window) {
                this.observer = new IntersectionObserver(this.onIntersect);
                this.observer.observe(this);
            }
            this.tick();
            this.interval = setInterval(this.tick, 1e3);
        }
        disconnectedCallback() {
            clearInterval(this.interval);
            this.interval = null;
            this.observer?.disconnect();
            this.observer = null;
        }
        onIntersect(entries) {
            this.visible = entries.some(entry => entry.isIntersecting);
            if (this.visible) {
                this.forceAria = true;
                this.tick();
            }
        }
        tick() {
            const remaining = Math.max(0, Math.floor(this.deadline - Date.now() / 1e3));
            if (remaining === 0) {
                this.expire();
                return;
            }
            const showDays = remaining >= DAY_SECONDS;
            if (showDays !== this.showDays) {
                this.showDays = showDays;
                this.units.get("days")?.toggleAttribute("hidden", !showDays);
                this.separators.get("days")?.toggleAttribute("hidden", !showDays);
                this.captions.get("days")?.toggleAttribute("hidden", !showDays);
                this.units.get("seconds")?.toggleAttribute("hidden", showDays);
                this.separators.get("seconds")?.toggleAttribute("hidden", showDays);
                this.captions.get("seconds")?.toggleAttribute("hidden", showDays);
            }
            this.ordinal = 0;
            this.render("days", String(Math.floor(remaining / DAY_SECONDS)));
            this.render("hours", String(Math.floor(remaining % DAY_SECONDS / 3600)).padStart(2, "0"));
            this.render("minutes", String(Math.floor(remaining % 3600 / 60)).padStart(2, "0"));
            this.render("seconds", String(remaining % 60).padStart(2, "0"));
            this.updateAccessibleName(remaining);
        }
        render(unit, value) {
            const el = this.units.get(unit);
            if (!el) return;
            if (!this.rolling) {
                if (el.textContent !== value) el.textContent = value;
                return;
            }
            if (el.hidden) return;
            const digits = this.digits.get(unit);
            const current = digits.map(digit => digit.textContent).join("");
            if (current === value) return;
            if (value.length !== digits.length) {
                this.digits.set(unit, buildDigitCells(el, value));
                return;
            }
            for (let i = 0; i < value.length; i += 1) if (value[i] !== current[i]) this.animateDigit(digits[i], value[i], unit);
        }
        animateDigit(digitEl, newChar, unit) {
            if (this.reducedMotion.matches) {
                digitEl.textContent = newChar;
                return;
            }
            const rarity = RARITY[unit];
            const params = {
                duration: ROLL.duration * rarity,
                distance: ROLL.distance * rarity,
                blur: ROLL.blur * rarity
            };
            if (params.distance > 0 && digitEl.textContent === "0" && newChar === "9") params.distance *= ROLLOVER_BOOST;
            const cell = digitEl.parentElement;
            cell.style.overflow = ROLL.clip ? "clip" : "";
            rollDigit(cell, digitEl, newChar, params, this.ordinal * STAGGER_MS);
            this.ordinal += 1;
        }
        updateAccessibleName(remaining) {
            const active = document.activeElement;
            const focused = active && active !== document.body && (this.contains(active) || active.contains(this));
            if (!this.visible && !focused) return;
            const bucket = Math.floor(remaining / 60);
            if (!this.forceAria && bucket === this.lastAriaBucket) return;
            this.lastAriaBucket = bucket;
            this.forceAria = false;
            const strings = window.MBF?.strings?.countdown;
            if (!strings) return;
            this.setAttribute("aria-label", composeAriaName(strings, this.dataset.label, remaining));
        }
        expire() {
            clearInterval(this.interval);
            this.interval = null;
            this.observer?.disconnect();
            this.observer = null;
            const item = this.closest("[data-countdown-item]");
            if (item) item.remove(); else this.remove();
            document.dispatchEvent(new CustomEvent("countdown:expired", {
                detail: {
                    deadline: this.deadline
                }
            }));
        }
    });
})();